<script setup lang="ts">
/**
 * 组件展示 Tab
 * 展示 app/platform/design/components 目录下的所有组件
 */

// Import components from platform/design/components
import AdvantageCard from '~/design/components/advantage-card/index.vue';
import CommonCarousel from '~/design/components/common-carousel/index.vue';
import CoreTechCard from '~/design/components/core-tech-card/index.vue';
import EditCard from '~/design/components/edit-card/index.vue';
import FeatureCard from '~/design/components/feature-card/index.vue';
import MarqueeSwiper from '~/design/components/marquee-swiper/index.vue';
import StartBtn from '~/design/components/start-btn/index.vue';
import StudioCard from '~/design/components/studio-card/index.vue';
import ToneCard from '~/design/components/tone-card/index.vue';

// Import button components
import ArrowBtn from '~/design/components/buttons/arrow-btn.vue';
import BaseBtn from '~/design/components/buttons/base.vue';
import BookBtn from '~/design/components/buttons/book-btn.vue';
import CreateBtn from '~/design/components/buttons/create-btn.vue';
import LoginBtn from '~/design/components/buttons/login-btn.vue';
import PlayBtn from '~/design/components/buttons/play-btn.vue';

// Import icons for AdvantageCard
import IconControl from '~/design/icons/fully-control.svg?component';
import IconEfficiency from '~/design/icons/high-efficiency.svg?component';
import IconQuality from '~/design/icons/high-quality.svg?component';

// Import getFeUrl helper
import { getFeUrl } from '~/utils/url';

// Real data from platform pages
const advantageCardData = {
  icon: IconQuality,
  title: '高质量',
  desc: '从灯光、场景，到人物形象、表现和运镜均达到专业级视频水准，成片效果媲美专业视频团队。',
};

const coreTechCardData = {
  title: '3D 角色引擎',
  desc: '内置数千款超写实 3D 数字人角色，覆盖不同年龄、族裔与风格；支持个性化形象编辑与 3D 数字人形象克隆，快速生成专属 3D 形象。',
  videoSrc: getFeUrl('/youyan-platform/api-intro/core-techs/core-tech-1.mp4'),
  posterSrc: getFeUrl(
    '/youyan-platform/api-intro/core-techs/core-tech-1.png?x-oss-process=image/format,webp',
  ),
};

const editCardData = {
  title: 'PPT生成视频',
  des: '上传你的PPT，或使用有言的AIPPT功能，无需专业学习即可轻松生成高质量的PPT数字人讲解视频。',
  path: getFeUrl(
    '/youyan-platform/ai-voice/personalization/voice-clone.png?x-oss-process=image/format,webp',
  ),
};

const featureCardData = {
  title: '影视级超写实画质\n打造极致沉浸感',
  description:
    '基于强大的3D场景构建能力，我们提供拥有逼真光影与细节的超写实背景。相较于普通2D背景，它能带来无与伦比的空间感与沉浸感。',
  image: getFeUrl('/youyan-platform/scene/profession/1.jpg?x-oss-process=image/format,webp'),
  btnText: '免费试用',
};

const studioCardData = {
  title: '白色流线概念风发布会场',
  description:
    '这是一个带有大屏幕的舞台场景，简约科技感的视觉风格，可以让视频非常吸引观众的注意力，可以用于制作高级的发布会讲解视频。',
  coverUrl: getFeUrl('/youyan-platform/scene/scene-library/white-streamline-concept-venue.png'),
  tags: ['大舞台', '科幻抽象', '企业营销'],
  identity: 'lstage_simple_white_01',
};

// ToneCard data - from ai-voice-cn/modules/tone-library
const toneCardSamples = [
  {
    name: '热心学姐',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__26_pro.wav',
    id: 'XMOV_HN_TTS__26',
    gender: 'Female' as const,
    subtitle: '青年分享 阳光开朗',
  },
  {
    name: '专业女声',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__8_pro.wav',
    id: 'XMOV_HN_TTS__8',
    gender: 'Female' as const,
    subtitle: '青年讲解 专业干练',
  },
  {
    name: '阳光男孩',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__18_pro.wav',
    id: 'XMOV_HN_TTS__18',
    gender: 'Male' as const,
    subtitle: '青年种草 阳光开朗',
  },
];

// MarqueeSwiper data - from chat-ppt/components/section-industry
const marqueeSwiperItems = [
  {
    img: getFeUrl(
      '/youyan-platform/main/what-makes-rendora-so-powerful/3-1.png?x-oss-process=image/format,webp',
    ),
    title: '智能投顾助手',
    desc: '依托AIGC智能建模&PPT自动化解答理财基础问题：赋能理财投顾，服务更多客户。',
    bgcolor: 'rgba(255, 241, 240, 1)',
  },
  {
    img: getFeUrl(
      '/youyan-platform/main/what-makes-rendora-so-powerful/3-2.png?x-oss-process=image/format,webp',
    ),
    title: 'AI助教/老师',
    desc: '课件PPT生成个性化答疑助手：无需老师1对1反复回答学生问题。',
    bgcolor: 'rgba(236, 247, 245, 1)',
  },
  {
    img: getFeUrl(
      '/youyan-platform/main/what-makes-rendora-so-powerful/3-3.png?x-oss-process=image/format,webp',
    ),
    title: 'AI产品导购',
    desc: '复杂产品PPT变身智能讲解员：产品型号功能复杂，传统导购讲不清楚怎么办？',
    bgcolor: 'rgba(240, 242, 245, 1)',
  },
];

// CommonCarousel data
const commonCarouselItems = [
  {
    image: getFeUrl('/youyan-platform/scene/profession/1.jpg?x-oss-process=image/format,webp'),
    title: '专业拍摄',
    desc: '影视级布光与运镜',
  },
  {
    image: getFeUrl('/youyan-platform/scene/profession/2.jpg?x-oss-process=image/format,webp'),
    title: 'AI 驱动',
    desc: '全自动视频生成',
  },
  {
    image: getFeUrl('/youyan-platform/scene/profession/1.jpg?x-oss-process=image/format,webp'),
    title: '海量素材',
    desc: '丰富的场景与角色库',
  },
];
</script>

<template>
  <div class="components-tab">
    <div class="components-tab__header">
      <h1>基础组件</h1>
      <p class="components-tab__desc">
        展示 <code>app/platform/design/components</code> 目录下的所有组件
      </p>
    </div>

    <div class="components-tab__content">
      <!-- Buttons -->
      <div class="demo-block">
        <h3>Buttons - 按钮组件</h3>
        <p class="demo-desc">各种样式和变体的按钮组件</p>
        <div class="demo-preview button-showcase">
          <div class="button-group">
            <h4>ArrowBtn - 箭头按钮</h4>
            <div class="button-row">
              <ArrowBtn variant="black-common" text="黑色常规" />
              <ArrowBtn variant="black-middle" text="黑色中等" />
              <ArrowBtn variant="purple-common" text="紫色常规" />
              <ArrowBtn variant="border-white-middle" text="白色边框" />
            </div>
          </div>
          <div class="button-group">
            <h4>其他按钮</h4>
            <div class="button-row">
              <BaseBtn variant="black-common">基础按钮</BaseBtn>
              <BookBtn :code="1" />
              <CreateBtn />
              <LoginBtn />
              <PlayBtn class="!w-[64px] !h-[64px]" :is-playing="false" />
            </div>
          </div>
        </div>
        <div class="code-preview">
          <pre><code>&lt;ArrowBtn variant="black-common" text="黑色常规" /&gt;
&lt;ArrowBtn variant="purple-common" text="紫色常规" /&gt;
&lt;BaseBtn&gt;基础按钮&lt;/BaseBtn&gt;
&lt;BookBtn /&gt;
&lt;CreateBtn /&gt;
&lt;LoginBtn /&gt;</code></pre>
        </div>
      </div>

      <!-- Advantage Card -->
      <div class="demo-block">
        <h3>Advantage Card - 优势卡片</h3>
        <p class="demo-desc">用于展示产品优势或核心特性的卡片组件，支持图标、标题和描述</p>
        <div class="demo-preview card-grid">
          <AdvantageCard v-bind="advantageCardData" />
          <AdvantageCard
            :icon="IconEfficiency"
            title="高效率"
            desc="从脚本到成片仅需数秒，轻松应对大批量、高频次的内容生产需求。"
          />
          <AdvantageCard
            :icon="IconControl"
            title="高可控"
            desc="支持对场景、角色、音色、动作、镜头所有视频元素的任意编辑，实现视频内容的精细化控制。"
          />
        </div>
        <div class="code-preview">
          <pre><code>&lt;AdvantageCard
  :icon="IconQuality"
  title="高质量"
  desc="从灯光、场景，到人物形象、表现和运镜均达到专业级视频水准。"
/&gt;</code></pre>
        </div>
      </div>

      <!-- Core Tech Card -->
      <div class="demo-block">
        <h3>Core Tech Card - 核心技术卡片</h3>
        <p class="demo-desc">展示核心技术特性的卡片，支持视频展示</p>
        <div class="demo-preview">
          <CoreTechCard v-bind="coreTechCardData" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;CoreTechCard
  title="3D 角色引擎"
  desc="内置数千款超写实 3D 数字人角色..."
  videoSrc="video-url.mp4"
  posterSrc="poster-url.png"
/&gt;</code></pre>
        </div>
      </div>

      <!-- Edit Card -->
      <div class="demo-block">
        <h3>Edit Card - 编辑卡片</h3>
        <p class="demo-desc">用于展示功能特性的卡片组件，支持自定义按钮插槽</p>
        <div class="demo-preview">
          <EditCard :data="editCardData" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;EditCard :data="{
  title: 'PPT生成视频',
  des: '上传你的PPT，无需专业学习即可轻松生成高质量的PPT数字人讲解视频。',
  path: 'image-url.png'
}" /&gt;</code></pre>
        </div>
      </div>

      <!-- Feature Card -->
      <div class="demo-block">
        <h3>Feature Card - 特性卡片</h3>
        <p class="demo-desc">展示产品特性的大型卡片，支持图文布局</p>
        <div class="demo-preview">
          <FeatureCard v-bind="featureCardData" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;FeatureCard
  title="影视级超写实画质"
  description="基于强大的3D场景构建能力..."
  image="image-url.jpg"
  btnText="免费试用"
/&gt;</code></pre>
        </div>
      </div>

      <!-- Studio Card -->
      <div class="demo-block">
        <h3>Studio Card - 工作室卡片</h3>
        <p class="demo-desc">展示场景/工作室的卡片组件，带标签和描述</p>
        <div class="demo-preview">
          <StudioCard v-bind="studioCardData" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;StudioCard
  title="白色流线概念风发布会场"
  description="这是一个带有大屏幕的舞台场景..."
  coverUrl="cover-url.png"
  :tags="['大舞台', '科幻抽象', '企业营销']"
  identity="lstage_simple_white_01"
/&gt;</code></pre>
        </div>
      </div>

      <!-- Marquee Swiper -->
      <div class="demo-block">
        <h3>Marquee Swiper - 跑马灯轮播</h3>
        <p class="demo-desc">自动滚动的轮播组件，常用于展示多个内容卡片</p>
        <div class="demo-preview" style="max-width: 100%; overflow: hidden">
          <MarqueeSwiper :items="marqueeSwiperItems" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;MarqueeSwiper :items="items" /&gt;</code></pre>
        </div>
      </div>

      <!-- Start Button -->
      <div class="demo-block">
        <h3>Start Button - 开始按钮</h3>
        <p class="demo-desc">引导用户开始操作的按钮</p>
        <div class="demo-preview">
          <StartBtn />
        </div>
        <div class="code-preview">
          <pre><code>&lt;StartBtn /&gt;</code></pre>
        </div>
      </div>

      <!-- Tone Card -->
      <div class="demo-block">
        <h3>Tone Card - 音色卡片</h3>
        <p class="demo-desc">展示音色信息的卡片，支持音频播放</p>
        <div class="demo-preview card-grid" style="grid-template-columns: repeat(3, 1fr)">
          <ToneCard v-for="(item, index) in toneCardSamples" :key="index" :item="item" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;ToneCard :item="{
  name: '热心学姐',
  audioProUrl: 'https://...',
  id: 'XMOV_HN_TTS__26',
  gender: 'Female',
  subtitle: '青年分享 阳光开朗'
}" /&gt;</code></pre>
        </div>
      </div>

      <!-- Common Carousel -->
      <div class="demo-block">
        <h3>Common Carousel - 通用轮播</h3>
        <p class="demo-desc">通用的轮播图组件</p>
        <div class="demo-preview">
          <CommonCarousel :items="commonCarouselItems">
            <template #item="{ item }">
              <div class="carousel-item">
                <img :src="item.image" :alt="item.title" class="carousel-img" />
                <div class="carousel-info">
                  <h4>{{ item.title }}</h4>
                  <p>{{ item.desc }}</p>
                </div>
              </div>
            </template>
          </CommonCarousel>
        </div>
        <div class="code-preview">
          <pre><code>&lt;CommonCarousel /&gt;</code></pre>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.components-tab {
  width: 100%;
  padding: 24px;

  &__header {
    margin-bottom: 32px;

    h1 {
      margin: 0 0 8px 0;
      font-size: 28px;
      font-weight: 700;
      color: var(--x-text-1);
    }
  }

  &__desc {
    margin: 0;
    font-size: 14px;
    color: var(--x-text-4);

    code {
      padding: 2px 6px;
      background: var(--x-back-3);
      border-radius: 4px;
      font-family: 'Monaco', 'Courier New', monospace;
      font-size: 13px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 1440px;
    margin: 0 auto;
  }
}

.demo-block {
  padding: 24px;
  background: var(--x-back-1);
  border: 1px solid var(--x-back-4);
  border-radius: 8px;

  h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--x-text-1);
  }

  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 500;
    color: var(--x-text-2);
  }
}

.demo-desc {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: var(--x-text-4);
}

.demo-preview {
  padding: 24px;
  background: var(--x-back-2);
  border-radius: 6px;
  margin-bottom: 16px;

  &.button-showcase {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &.card-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
  }
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.button-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.code-preview {
  pre {
    margin: 0;
    padding: 16px;
    background: var(--x-back-3);
    border-radius: 6px;
    overflow-x: auto;

    code {
      font-family: 'Monaco', 'Courier New', monospace;
      font-size: 13px;
      color: var(--x-text-2);
      line-height: 1.6;
    }
  }
}

.carousel-item {
  width: 400px;
  height: 240px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  background: var(--x-back-2);

  .carousel-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .carousel-info {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
    color: #fff;

    h4 {
      margin: 0 0 4px 0;
      font-size: 16px;
      font-weight: 600;
    }

    p {
      margin: 0;
      font-size: 13px;
      opacity: 0.9;
    }
  }

  &:hover .carousel-img {
    transform: scale(1.05);
  }
}
</style>
